import { type CSSProperties, type RefObject, useCallback, useEffect, useRef, useState } from "react"

import { createPortal } from "react-dom"

import type { Document } from "@/api/models"

import { getCitationFilePath } from "@/api/api"
import { isPersonalLibraryCitation } from "@/features/ai-explorer/utils/personal-search-utils"
import { findLibraryDocumentForCitation, parseCitationParts } from "@/utils/citation-utils"

import { CitationHoverExcelMiniViewer } from "./citation-hover-excel-mini-viewer"
import { CitationHoverMiniViewer } from "./citation-hover-mini-viewer"
import {
  getCitationTitle,
  parseCitationIndex,
  resolveHighlightSnippets,
} from "./citation-inline-peek-utils"
import { CitationPeekContent } from "./citation-peek-content"
import { getCitationType } from "./citation.utils"
import { extractChunkTextForHighlight } from "../hooks/use-chunk-finder"
import { useHeavyPreviewGate } from "../hooks/use-heavy-preview-gate"
import {
  getHoverMiniPreviewMode,
  getHoverMiniRendererDelayMs,
  type HoverMiniPreviewMode,
  isHeavyPreviewExtension,
} from "../utils/citation-hover-mini-renderer"

type ChunkData = {
  text: string[]
  title?: string
  images?: { url: string }[]
}

type CitationInlinePeekProps = {
  answerTextRef: RefObject<HTMLDivElement | null>
  citations: string[]
  citationSection: string
  findChunkForUrlCitation: (citation: string) => ChunkData | null
  onViewDocument: (
    citation: string,
    citationSection: string,
    options?: { chunkText?: string; documents?: [string, Document][] }
  ) => void
  documents?: [string, Document][]
  disabled?: boolean
}

type HoverState = {
  citation: string
  title: string
  pageNumber: number | null
  documentUrl: string
  chunkText: string | null
  quoteText: string | null
  quoteSnippet: string
  chunkSnippet: string
  miniPreviewMode: HoverMiniPreviewMode
  placement: "below" | "above"
  anchorRect: DOMRect
}

const OPEN_DELAY_MS = 400
const CLOSE_DELAY_MS = 150
const CARD_COLLAPSED_WIDTH_PX = 320
const CARD_EXPANDED_WIDTH_PX = 520
const EDGE_MARGIN_PX = 12
const CARD_OFFSET_PX = 8
const CARD_COLLAPSED_MAX_HEIGHT_PX = 220
const CARD_EXPANDED_MAX_HEIGHT_PX = 560

function getPreferredPlacement(anchorRect: DOMRect): "below" | "above" {
  const availableBelow = window.innerHeight - anchorRect.bottom - CARD_OFFSET_PX - EDGE_MARGIN_PX
  const availableAbove = anchorRect.top - CARD_OFFSET_PX - EDGE_MARGIN_PX
  return availableBelow >= availableAbove ? "below" : "above"
}

function getCardLayout(
  anchorRect: DOMRect,
  widthPx: number,
  layoutOptions: { targetMaxHeightPx: number; placement: "below" | "above" }
): { left: number; maxHeight: number; top?: number; bottom?: number } {
  const { targetMaxHeightPx, placement } = layoutOptions
  const maxLeft = Math.max(EDGE_MARGIN_PX, window.innerWidth - widthPx - EDGE_MARGIN_PX)
  const left = Math.min(Math.max(anchorRect.left, EDGE_MARGIN_PX), maxLeft)

  const availableBelow = Math.max(
    0,
    window.innerHeight - anchorRect.bottom - CARD_OFFSET_PX - EDGE_MARGIN_PX
  )
  const availableAbove = Math.max(0, anchorRect.top - CARD_OFFSET_PX - EDGE_MARGIN_PX)
  const availableOnSide = placement === "below" ? availableBelow : availableAbove
  const maxHeight = Math.max(0, Math.min(targetMaxHeightPx, availableOnSide))

  if (placement === "below") {
    const naturalTop = anchorRect.bottom + CARD_OFFSET_PX
    const maxBottomTop = window.innerHeight - maxHeight - EDGE_MARGIN_PX
    const top = Math.max(EDGE_MARGIN_PX, Math.min(naturalTop, maxBottomTop))
    return { left, maxHeight, top }
  }

  // Anchor above by its bottom edge so growth doesn't pull it farther upward.
  const naturalBottom = window.innerHeight - anchorRect.top + CARD_OFFSET_PX
  const bottom = Math.max(EDGE_MARGIN_PX, naturalBottom)
  return { left, maxHeight, bottom }
}

export function CitationInlinePeek({
  answerTextRef,
  citations,
  citationSection,
  findChunkForUrlCitation,
  onViewDocument,
  documents,
  disabled = false,
}: CitationInlinePeekProps) {
  const { heavyPreviewsGated, onSlowRender: scheduleHeavyPreviewAutoGate } = useHeavyPreviewGate()

  const [hoverState, setHoverState] = useState<HoverState | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isMiniViewerReady, setIsMiniViewerReady] = useState(false)
  const [isClient, setIsClient] = useState(false)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const hoveredButtonRef = useRef<HTMLElement | null>(null)
  const openTimeoutRef = useRef<number | null>(null)
  const closeTimeoutRef = useRef<number | null>(null)
  const miniViewerTimeoutRef = useRef<number | null>(null)

  const clearOpenTimeout = useCallback(() => {
    if (openTimeoutRef.current !== null) {
      window.clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
  }, [])

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  const clearMiniViewerTimeout = useCallback(() => {
    if (miniViewerTimeoutRef.current !== null) {
      window.clearTimeout(miniViewerTimeoutRef.current)
      miniViewerTimeoutRef.current = null
    }
  }, [])

  const closePeek = useCallback(() => {
    clearOpenTimeout()
    clearCloseTimeout()
    clearMiniViewerTimeout()
    hoveredButtonRef.current = null
    setIsExpanded(false)
    setIsMiniViewerReady(false)
    setHoverState(null)
  }, [clearCloseTimeout, clearMiniViewerTimeout, clearOpenTimeout])

  const scheduleClose = useCallback(() => {
    clearCloseTimeout()
    closeTimeoutRef.current = window.setTimeout(() => {
      clearMiniViewerTimeout()
      hoveredButtonRef.current = null
      setIsExpanded(false)
      setIsMiniViewerReady(false)
      setHoverState(null)
    }, CLOSE_DELAY_MS)
  }, [clearCloseTimeout, clearMiniViewerTimeout])

  const resolveHoverState = useCallback(
    (button: HTMLElement): HoverState | null => {
      const citationIndex = parseCitationIndex(button)
      const citationFromData = button.dataset.citationRaw
      const hasRawCitation =
        typeof citationFromData === "string" && citationFromData.trim().length > 0
      const citationFromIndex = citationIndex !== null ? citations[citationIndex] : undefined
      const citation = hasRawCitation ? citationFromData : citationFromIndex

      if (!citation) {
        return null
      }

      if (getCitationType(citation) !== "library" || isPersonalLibraryCitation(citation)) {
        return null
      }

      const parsedCitation = parseCitationParts(citation)
      const chunk = findChunkForUrlCitation(citation)
      const chunkText = extractChunkTextForHighlight(chunk, citation)
      const matchingDocument = findLibraryDocumentForCitation(citation, documents)
      const documentUrl =
        getCitationFilePath(parsedCitation.source, citationSection, matchingDocument) +
        (parsedCitation.page != null ? `#page=${parsedCitation.page}` : "")
      const { quoteSnippet, chunkSnippet } = resolveHighlightSnippets(
        parsedCitation.quote,
        chunkText
      )
      const { title, pageNumber } = getCitationTitle(citation, documents)
      let miniPreviewMode = getHoverMiniPreviewMode(documentUrl)
      if (heavyPreviewsGated && isHeavyPreviewExtension(documentUrl)) {
        miniPreviewMode = "none"
      }
      const anchorRect = button.getBoundingClientRect()

      return {
        citation,
        title,
        pageNumber,
        documentUrl,
        chunkText,
        quoteText: parsedCitation.quote,
        quoteSnippet,
        chunkSnippet,
        miniPreviewMode,
        placement: getPreferredPlacement(anchorRect),
        anchorRect,
      }
    },
    [citationSection, citations, documents, findChunkForUrlCitation, heavyPreviewsGated]
  )

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (disabled) {
      closePeek()
    }
  }, [closePeek, disabled])

  useEffect(() => {
    const answerRoot = answerTextRef.current
    if (!answerRoot || disabled) {
      return
    }

    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) {
        return
      }

      const button = target.closest(".supContainer")
      if (!(button instanceof HTMLElement) || !answerRoot.contains(button)) {
        return
      }

      if (hoveredButtonRef.current === button && hoverState) {
        return
      }

      hoveredButtonRef.current = button
      clearCloseTimeout()
      clearOpenTimeout()

      const nextState = resolveHoverState(button)
      if (!nextState) {
        return
      }

      openTimeoutRef.current = window.setTimeout(() => {
        if (hoveredButtonRef.current !== button) {
          return
        }
        setIsExpanded(false)
        setIsMiniViewerReady(false)
        setHoverState(nextState)
      }, OPEN_DELAY_MS)
    }

    const handleMouseOut = (event: MouseEvent) => {
      const related = event.relatedTarget
      if (related instanceof Node && cardRef.current?.contains(related)) {
        return
      }
      if (related instanceof Element && answerRoot.contains(related.closest(".supContainer"))) {
        return
      }
      scheduleClose()
    }

    answerRoot.addEventListener("mouseover", handleMouseOver)
    answerRoot.addEventListener("mouseout", handleMouseOut)

    return () => {
      answerRoot.removeEventListener("mouseover", handleMouseOver)
      answerRoot.removeEventListener("mouseout", handleMouseOut)
      clearOpenTimeout()
      clearCloseTimeout()
      clearMiniViewerTimeout()
    }
  }, [
    answerTextRef,
    clearCloseTimeout,
    clearMiniViewerTimeout,
    clearOpenTimeout,
    disabled,
    hoverState,
    resolveHoverState,
    scheduleClose,
  ])

  useEffect(() => {
    if (!hoverState) {
      return
    }

    const updateAnchor = () => {
      if (!hoveredButtonRef.current) {
        setHoverState(null)
        return
      }

      const nextRect = hoveredButtonRef.current.getBoundingClientRect()
      setHoverState((previous) => {
        if (!previous) return previous
        const topDelta = Math.abs(previous.anchorRect.top - nextRect.top)
        const leftDelta = Math.abs(previous.anchorRect.left - nextRect.left)
        const widthDelta = Math.abs(previous.anchorRect.width - nextRect.width)
        const heightDelta = Math.abs(previous.anchorRect.height - nextRect.height)
        if (topDelta < 1 && leftDelta < 1 && widthDelta < 1 && heightDelta < 1) {
          return previous
        }
        return { ...previous, anchorRect: nextRect }
      })
    }

    window.addEventListener("scroll", updateAnchor)
    window.addEventListener("resize", updateAnchor)
    return () => {
      window.removeEventListener("scroll", updateAnchor)
      window.removeEventListener("resize", updateAnchor)
    }
  }, [hoverState])

  useEffect(() => {
    const citationIdentity = hoverState ? `${hoverState.citation}|${hoverState.documentUrl}` : null
    const miniPreviewMode = hoverState?.miniPreviewMode ?? "none"

    clearMiniViewerTimeout()
    if (!citationIdentity || miniPreviewMode === "none") {
      setIsMiniViewerReady(false)
      return
    }

    setIsMiniViewerReady(false)
    miniViewerTimeoutRef.current = window.setTimeout(() => {
      if (!hoveredButtonRef.current) {
        return
      }
      setIsMiniViewerReady(true)
      setIsExpanded(true)
    }, getHoverMiniRendererDelayMs())

    return () => {
      clearMiniViewerTimeout()
    }
  }, [
    clearMiniViewerTimeout,
    hoverState?.citation,
    hoverState?.documentUrl,
    hoverState?.miniPreviewMode,
  ])

  useEffect(() => {
    setHoverState(null)
    setIsExpanded(false)
    setIsMiniViewerReady(false)
    clearMiniViewerTimeout()
    hoveredButtonRef.current = null
  }, [citations, clearMiniViewerTimeout])

  const onHeavyHoverSlowRender = useCallback(() => {
    if (heavyPreviewsGated) {
      return
    }
    scheduleHeavyPreviewAutoGate()
    setHoverState((prev) => {
      if (!prev || !isHeavyPreviewExtension(prev.documentUrl)) {
        return prev
      }
      if (prev.miniPreviewMode === "none") {
        return prev
      }
      return { ...prev, miniPreviewMode: "none" }
    })
  }, [heavyPreviewsGated, scheduleHeavyPreviewAutoGate])

  const hoverSlowRenderCallback: (() => void) | undefined =
    hoverState != null && !heavyPreviewsGated && isHeavyPreviewExtension(hoverState.documentUrl)
      ? onHeavyHoverSlowRender
      : undefined

  if (!isClient || !hoverState || disabled) {
    return null
  }

  const cardWidth = Math.min(
    isExpanded ? CARD_EXPANDED_WIDTH_PX : CARD_COLLAPSED_WIDTH_PX,
    window.innerWidth - EDGE_MARGIN_PX * 2
  )
  const showMiniViewer = isExpanded && hoverState.miniPreviewMode !== "none" && isMiniViewerReady
  const layout = getCardLayout(hoverState.anchorRect, cardWidth, {
    targetMaxHeightPx: isExpanded ? CARD_EXPANDED_MAX_HEIGHT_PX : CARD_COLLAPSED_MAX_HEIGHT_PX,
    placement: hoverState.placement,
  })
  const openDocumentFromHover = () => {
    const options = {
      chunkText: hoverState.chunkText ?? undefined,
      ...(documents ? { documents } : {}),
    }
    onViewDocument(hoverState.citation, citationSection, options)
    closePeek()
  }

  const cardStyle: CSSProperties = {
    bottom: layout.bottom !== undefined ? `${layout.bottom}px` : undefined,
    left: `${layout.left}px`,
    maxHeight: `${layout.maxHeight}px`,
    overflowY: isExpanded ? "auto" : "hidden",
    position: "fixed",
    top: layout.top !== undefined ? `${layout.top}px` : undefined,
    transition: "width 140ms ease, max-height 180ms ease",
    width: `${cardWidth}px`,
  }

  return createPortal(
    <div
      ref={cardRef}
      className="bg-popover text-popover-foreground z-[60] w-80 cursor-pointer rounded-md border p-4 shadow-md"
      role="button"
      style={cardStyle}
      tabIndex={0}
      onClick={openDocumentFromHover}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          openDocumentFromHover()
        }
      }}
      onMouseEnter={() => {
        clearCloseTimeout()
      }}
      onMouseLeave={() => {
        setIsExpanded(false)
        setIsMiniViewerReady(false)
        scheduleClose()
      }}
    >
      <CitationPeekContent
        chunkText={hoverState.chunkText}
        expanded={isExpanded}
        hidePreviewText={showMiniViewer}
        interactionMode="static"
        pageNumber={hoverState.pageNumber}
        quoteText={hoverState.quoteText}
        title={hoverState.title}
        onViewDocument={openDocumentFromHover}
      />
      {isExpanded && hoverState.miniPreviewMode !== "none" && (
        <div className="mt-3 [&_*]:pointer-events-none">
          {hoverState.miniPreviewMode === "excel" ? (
            <CitationHoverExcelMiniViewer
              chunkSnippet={hoverState.chunkSnippet}
              documentUrl={hoverState.documentUrl}
              enabled={isMiniViewerReady}
              quoteSnippet={hoverState.quoteSnippet}
              onSlowRender={hoverSlowRenderCallback}
            />
          ) : (
            <CitationHoverMiniViewer
              chunkSnippet={hoverState.chunkSnippet}
              documentUrl={hoverState.documentUrl}
              enabled={isMiniViewerReady}
              quoteSnippet={hoverState.quoteSnippet}
              onSlowRender={hoverSlowRenderCallback}
            />
          )}
        </div>
      )}
    </div>,
    document.body
  )
}
